import { Array, Filter, Function, identity, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'

import { CORE_CONSTANT } from '../../../constant.ts'
import type {
  ViewQueryAggregation,
  ViewQueryDimensionField,
  ViewQueryFilterNode,
  ViewQueryList,
  ViewQueryNode,
  ViewQueryNodeReadJoinOn,
  ViewQuerySorter,
} from '../../../schemas/query/index.ts'
import { makeIdentifiable } from '../../util/makeIdentifiable.ts'
import type { SqlPredicate } from '../sqlPredicate.ts'
import type { ViewResultCursorOrInitialCursor } from '../types.ts'
import { flatNodes, generateAbsoluteFieldAsName, generateAbsoluteFields, getLabeledName } from './util.ts'

// ノード実体からそのノードのテーブル参照を絞り込む WHERE 述語を導く。undefined = そのノードへの述語注入なし。
// ノード名文字列をキーとする Record 形にしないのは、名前の文字列照合を挟むと実在しないノード名への
// 紐付け (typo) が「注入なし」として silent に素通りし (fail-open)、それを防ぐ runtime の未知キー検査が
// 必要になるため。ノード実体を渡す契約なら実在しないノードに述語を紐付けること自体が構造的に起こらない。
// 述語内のカラム修飾はノード名で行う (物理テーブル名は不可)。注入後の FROM は
// (SELECT * FROM "物理名" AS "ノード名" WHERE 述語) AS "ノード名" の形になり、
// alias を付けた時点でサブクエリ内から物理テーブル名は参照できなくなるため。
export type NodePredicateResolver = (node: Pick<ViewQueryNode, 'name' | 'read'>) => SqlPredicate | undefined

// 「絞らない」を undefined ではなく名前付きの resolver で表すのは、呼び出し側が「絞らないと決めた経路」
// （閲覧ユーザーのいないシステム経路・運営 admin・機能フラグ OFF）であることを渡す値そのもので示せ、
// 生成側に undefined の分岐が要らなくなるため
export const unrestrictedNodePredicateResolver: NodePredicateResolver = Function.constUndefined

export interface GenerateSqlOption {
  shouldSkipRootSort?: boolean
  shouldGetAllFields?: boolean
  // optional キーにしないのは、可視性フィルタの渡し忘れが silent な全件公開（fail-open）になる代わりに
  // compile error になるようにするため。絞らない経路は unrestrictedNodePredicateResolver を明示して渡す
  resolveNodePredicate: NodePredicateResolver
}

export function generateSql(
  view: ViewQueryList,
  cursor: ViewResultCursorOrInitialCursor,
  mustacheParameter: Record<string, unknown>,
  option: GenerateSqlOption,
): string {
  const withSql = generateWithSql(view)
  const fields = generateFields(view)
  const filterSql = generateFilterSql(view.filterTree, 0, mustacheParameter)
  const joins = flatNodes(view.tree).map((node) =>
    generateJoinSql(node, mustacheParameter, option.resolveNodePredicate),
  )

  // 初回のoffsetは0、次回以降は前回取得分のレコードが含まれている可能性があるレコード分を取り直す形でoffsetを設定
  const offset = Math.max(cursor.pageSize * (cursor.page - 1) - cursor.chunkSize, 0)
  const limitAndOffset = `OFFSET ${offset} LIMIT ${cursor.pageSize + cursor.chunkSize}`
  const sorters = generateSorters(view.sorters, view.tree, option)
  const orderBy = sorters.length > 0 ? `ORDER BY ${sorters.join(', ')}` : ``
  const tableSqlWithAs = wrapNodeTableSql(view.tree, option.resolveNodePredicate)

  const sql = `${withSql}
SELECT
${option.shouldGetAllFields === true ? '*' : fields.join(`,\n`)}
FROM ${tableSqlWithAs}
${joins.join('\n')} ${filterSql !== undefined && filterSql !== '' ? `\nWHERE\n${filterSql}` : ``}
${view.extra?.asSubQuery === true ? `` : [orderBy, limitAndOffset].join('\n')}
`.replaceAll('\n\n', '\n')

  return sql
}

export function generateAggregationSql(
  view: ViewQueryAggregation,
  mustacheParameter: Record<string, unknown>,
  // unlimitedRowsEnabled は集約経路の LIMIT 制御にしか意味を持たないため共通 option に置かず、
  // generateSql へ渡す誤用を compile error にする交差型でこの関数に限定する
  option: GenerateSqlOption & { unlimitedRowsEnabled?: boolean },
): string {
  const withSql = generateWithSql(view)
  const fields = generateMeasures(view)
  const filterSql = generateFilterSql(view.filterTree, 0, mustacheParameter)
  const joins = Array.flatMapNullishOr(
    flatNodes(view.tree).map((node) => generateJoinSql(node, mustacheParameter, option.resolveNodePredicate)),
    identity,
  )
  const dimensions = generateDimensions(view)
  const orderBy = view.sorters.length > 0 ? `\nORDER BY ${generateSorters(view.sorters).join(', ')}` : ``
  const tableSqlWithAs = wrapNodeTableSql(view.tree, option.resolveNodePredicate)

  // 後続の処理で、 ${CORE_CONSTANT.AGGREGATION_QUERY_LIMIT} よりも多いレコードがあるかどうかを判定するため、
  // LIMIT ${CORE_CONSTANT.AGGREGATION_QUERY_LIMIT} + 1
  // としている。
  // 後続の処理でちゃんと ${CORE_CONSTANT.AGGREGATION_QUERY_LIMIT} 個でフィルターする。
  const sql = `${withSql}
SELECT
${[...dimensions.fields, ...fields].join(`,\n`)}
FROM ${tableSqlWithAs}
${joins.length > 0 ? joins.join('\n') : ''}${filterSql !== undefined && filterSql !== '' ? `\nWHERE\n${filterSql}` : ``}${
    dimensions.groupBy.length > 0 ? `\nGROUP BY ${dimensions.groupBy.join(', ')}` : ''
  }${orderBy}
${option.unlimitedRowsEnabled === true ? '' : `LIMIT ${CORE_CONSTANT.AGGREGATION_QUERY_LIMIT} + 1`}
`

  return sql.replaceAll('\n\n', '\n').replace(/^\n/v, '').replace(/\n$/v, '')
}

function generateWithSql(query: ViewQueryAggregation | ViewQueryList) {
  // サブクエリは見づらいので、WITHとして扱う
  const subQueryNodes = flatNodes(query.tree).filter((x) => x.read.tableType === 'subquery')
  if (subQueryNodes.length === 0) {
    return ``
  }
  const queries = subQueryNodes.map((x) => `${x.name} AS (${x.read.tableSql})`)

  return `WITH ${queries.join(',\n')}\n`
}

// 現状、フロントエンドで親ノードのIDをkeyとしてレンダリングしており、keyが複数あることを許していない。
function generateSorters(
  sorters: ViewQuerySorter[],
  rootNode?: ViewQueryNode,
  option?: { shouldSkipRootSort?: boolean },
): string[] {
  const [rootSorters, childSorters] = Array.partition(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    sorters ?? [],
    Filter.fromPredicate((sorter) => {
      if (sorter.nodePaths.length !== 1) {
        return false
      }
      const [nodePath] = sorter.nodePaths
      return nodePath?.length === 1
    }),
  )
  const xs = Array.flatMapNullishOr(
    [
      ...rootSorters.map((x) => `${x.read.labelSql ?? x.read.sql} ${x.read.order}`),
      rootNode !== undefined && option?.shouldSkipRootSort !== true ? `"${rootNode.name}".id` : undefined, // 常にidでソートし、隣接するidを後からアプリケーションレイヤでグルーピングする
      ...childSorters.map((x) => `${x.read.labelSql ?? x.read.sql} ${x.read.order}`),
    ],
    identity,
  )
  return xs
}

export function generateLabelSqlFieldName(fieldName: string) {
  return `${fieldName}_label`
}

function anyAggregation(fieldName: string) {
  return `MIN(${fieldName})`
}

function dimensionToField(dimension: ViewQueryDimensionField): string[] {
  return Array.flatMapNullishOr(
    [
      [dimension.sql, quote(dimension.name)].join(' AS '),
      dimension.labelSql === undefined
        ? undefined
        : [anyAggregation(dimension.labelSql), quote(generateLabelSqlFieldName(dimension.name))].join(' AS '),
    ],
    identity,
  )
}

function generateRollup(ds: ViewQueryDimensionField[], withoutRollup?: boolean) {
  const sqls = ds.filter((x) => x.isScalar !== true).map((x) => x.fieldSql)
  if (sqls.length === 0) {
    return
  }
  if (withoutRollup === true) {
    return sqls.join(', ')
  }
  return `ROLLUP(${sqls.join(', ')})`
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO: quote
// eslint-disable-next-line sonarjs/todo-tag
// TODO: __NULL__, __TOTAL__

function generateDimensions(view: ViewQueryAggregation) {
  const fields: string[] = [
    ...(view.pivot?.rows.flatMap((x): string[] => dimensionToField(x)) ?? []),
    ...(view.pivot?.columns.flatMap((x): string[] => dimensionToField(x)) ?? []),
    ...(view.pivot?.dimensionsWithoutRollup?.flatMap((x): string[] => dimensionToField(x)) ?? []),
  ]
  const groupBy: string[] = Array.flatMapNullishOr(
    [
      generateRollup(view.pivot?.rows ?? []),
      generateRollup(view.pivot?.columns ?? []),
      generateRollup(view.pivot?.dimensionsWithoutRollup ?? [], true),
    ],
    identity,
  )
  return {
    fields,
    groupBy,
  }
}

function generateMeasures(view: ViewQueryAggregation): string[] {
  return view.measures.map((x) => [x.sql, quote(x.name)].join(' AS '))
}

// eslint-disable-next-line sonarjs/function-return-type
function quote<T extends string | undefined>(x: T) {
  if (x === undefined) {
    return x
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return `"${x ?? ''}"`
}

function generateFields(view: ViewQueryList): string[] {
  // writeのための、各テーブルのidのカラム
  const idFields =
    view.extra?.asSubQuery === true
      ? []
      : flatNodes(view.tree).map((node) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
          if (node.read.idColumn === undefined) {
            // eslint-disable-next-line array-callback-return
            return
          }

          return {
            sql: generateAbsoluteFields(node.name, node.read.idColumn),
            name: makeIdentifiable(generateAbsoluteFieldAsName(node.name, node.read.idColumn)),
          }
        })

  // joinで使っているフィールド
  const joinFields =
    view.extra?.asSubQuery === true
      ? []
      : flatNodes(view.tree).flatMap((node) => {
          if (node.read.join === undefined) {
            // eslint-disable-next-line array-callback-return
            return
          }
          const { joinOn } = node.read.join
          if (joinOn.type === 'sql') {
            // eslint-disable-next-line array-callback-return
            return
          }

          return [
            {
              sql: generateAbsoluteFields(joinOn.currentNodeName, joinOn.currentNodeColumnName),
              name: makeIdentifiable(generateAbsoluteFieldAsName(joinOn.currentNodeName, joinOn.currentNodeColumnName)),
            },
            {
              sql: generateAbsoluteFields(joinOn.parentNodeName, joinOn.parentNodeColumnName),
              name: makeIdentifiable(generateAbsoluteFieldAsName(joinOn.parentNodeName, joinOn.parentNodeColumnName)),
            },
          ]
        })

  // fieldsで指定されたフィールド
  const viewFields = view.fields.flatMap((field) =>
    Array.flatMapNullishOr(
      [
        {
          // field.read.sqlは、テーブル名を含む完全パスであることを期待している
          sql: field.read.distinct === true ? `DISTINCT ${field.read.sql}` : field.read.sql, // 現状のdistinctのロジックがmeasuresのサブクエリ関連のみなので、雑に対応している
          name: field.name,
        },
        field.read.labelSql === undefined
          ? undefined
          : {
              sql: field.read.labelSql,
              name: getLabeledName(field.name),
            },
        ...(field.read.additionalFields ?? []).map((additionalField) => ({
          sql: additionalField.sql,
          name: additionalField.name,
        })),
      ],
      identity,
    ),
  )

  const fields = pipe(
    [...idFields, ...joinFields, ...viewFields],
    Array.filter(isNotNullish),
    Array.dedupeWith((a, b) => a.name === b.name),
    Array.map((x) => `${x.sql} AS ${quote(x.name)}`),
  )

  return fields
}

function indent(n: number) {
  return (n >= 0 ? Array.range(0, n) : Array.empty<number>()).map((x) => '  ').join('')
}

function wrapInParentheses(x: string) {
  return `(${x})`
}

const wrapTableSql = (tableReference: string, nodeName: string, predicate: SqlPredicate): string =>
  `(SELECT * FROM ${tableReference} WHERE ${predicate}) AS ${quote(nodeName)}`

export const wrapNodeTableSql = (
  node: Pick<ViewQueryNode, 'name' | 'read'>,
  resolveNodePredicate: GenerateSqlOption['resolveNodePredicate'],
): string => {
  const tableReference =
    node.read.tableType === 'subquery'
      ? // サブクエリは見づらいので、WITHで定義することにする。WITHのロジックは別で実行済みのはずなので、ここではnode.nameを呼ぶだけ
        quote(node.name)
      : // eslint-disable-next-line sonarjs/no-nested-conditional
        node.read.tableType === 'lateral_subquery'
        ? // lateral_subquery → インラインで展開
          `(${node.read.tableSql}) AS ${quote(node.name)}`
        : // eslint-disable-next-line sonarjs/no-nested-conditional
          node.read.tableSql === node.name
          ? quote(node.read.tableSql)
          : `${quote(node.read.tableSql)} AS ${quote(node.name)}`
  const predicate = resolveNodePredicate(node)
  if (predicate === undefined) {
    return tableReference
  }
  // 述語ありでも外側 alias（ノード名）を保持する。alias が変わると
  // 親子 join・SELECT 句のカラム参照（"node"."column"）が全て壊れるため
  return wrapTableSql(tableReference, node.name, predicate)
}

function generateJoinSql(
  node: ViewQueryNode,
  mustacheParameter: Record<string, unknown>,
  resolveNodePredicate: GenerateSqlOption['resolveNodePredicate'],
): string | undefined {
  const { join } = node.read
  if (join === undefined) {
    return undefined
  }
  const filter = generateFilterSql(join.joinFilterTree, 0, mustacheParameter)
  const getJoinOnSql = (joinOn: ViewQueryNodeReadJoinOn) => {
    if (joinOn.type === 'sql') {
      return joinOn.sql
    }
    return `"${joinOn.parentNodeName}"."${joinOn.parentNodeColumnName}" = "${joinOn.currentNodeName}"."${joinOn.currentNodeColumnName}"`
  }
  const on = `${getJoinOnSql(join.joinOn)}${filter !== undefined && filter !== '' ? ` AND ${wrapInParentheses(filter)}` : ``}`

  if (node.read.tableType === 'lateral_subquery') {
    // lateral_subquery は結合条件をサブクエリ内部に持つため ON true で結合
    return `${join.joinType} ${wrapNodeTableSql(node, resolveNodePredicate)} ON true`
  }

  return `${join.joinType} ${wrapNodeTableSql(node, resolveNodePredicate)} ON ${on}`
}

//
// 以下で「交換可能である」というのは、複数の式や項を論理演算子(AND/OR)で結合したとき、順番を並び替えても同じ結果が得られることを呼ぶ。
// （例1）式Aが「x = 1」、式Bが「y = 2」であるとき、 「A AND B」「B AND A」は同じ結果が得られるため、交換可能である。
// （例2）式Aが「x = 1 OR xx = 1」、式Bが「y = 2」であるとき、同じ結果は得られないため、交換可能ではない。
//       なぜなら、 x = 1 OR xx = 1 AND y = 2 は、 OR も AND も左結合のため (x = 1 OR xx = 1) AND y = 2 と解釈される一方、
//       y = 2 AND x = 1 OR xx = 1 は同じ理由で (y = 2 AND x = 1) OR xx = 1 と解釈されるからである。
//       参考: PostgreSQL 16 の演算子の結合方向と優先順位 https://www.postgresql.org/docs/16/sql-syntax-lexical.html#SQL-PRECEDENCE
// （例3）式Aが「(x = 1 OR xx = 1)」、式Bが「y = 2」であるとき、 交換可能である
//
// generateFilterSqlは、depth=1以上のときは常に交換可能な式を返す（＝括弧で括られたSQLを返す）
// NOTE: 以下の実装において、交換可能でない式が紛れ込んだ状態で、雑に論理演算子でjoinすると、上記の例2のようなケースが発生して不具合になりうるので注意すること
//

export function generateFilterSql(
  filterNode: ViewQueryFilterNode | undefined,
  depth: number,
  mustacheParameter: Record<string, unknown>,
): string | undefined {
  if (filterNode === undefined) {
    return undefined
  }

  if (filterNode.leafs.length === 0 && filterNode.children.length === 0) {
    return undefined
  }
  const leafSqls = filterNode.leafs.map((x) => {
    // パラメーターに依存するSQLのとき、パラメーターが指定されていなければ条件を無視する
    if ((x.read.dependedParameters ?? []).some((x) => mustacheParameter[x] === undefined)) {
      // eslint-disable-next-line array-callback-return
      return
    }
    return x.read.sql // read.sqlは交換可能であることを常に期待する（そうなっていない場合、compileConfig側のバグ）
  })

  const childrenSqls = filterNode.children.map((x) => generateFilterSql(x, depth + 1, mustacheParameter)) // depth>=1のときのgenerateFilterSqlのレスポンスは常に交換可能

  const sqls = Array.flatMapNullishOr([...leafSqls, ...childrenSqls], identity)
  if (sqls.length === 0) {
    return undefined
  }
  // このsqlの中身は全て交換可能であり、ここで論理演算子で結合しても問題がない。leafなのかchildrenなのかは区別する必要がない。
  const sql = sqls.join(`\n${indent(depth)}${filterNode.logicalOperator.toUpperCase()} `)
  return depth === 0 ? sql : wrapInParentheses(sql) // depth >= 1のときは常に交換可能にするため、括弧に括る。無駄に括られることもありうるが、SQLの読みやすさよりも実装のシンプルさと堅牢さを優先する
}
